import { useMemo } from 'react';

import { Card } from '@mesh.ai/rabbit';
import { L3_PATHS } from 'Deer/constants/routes';
import { useQuery } from 'components/hooks/useQuery';
import { useHistory, useLocation } from 'react-router-dom';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { REVIEWEE_LIST_COLUMN_DEF, TABLE_COLUMNS_DISPLAYNAME } from '../constant';
import { useGetPOARevieweeList } from '../hooks/dataHooks';
import { PlanOfActionCellRenderer, getCellValue } from './CellRenderer';

const ReviewListView = ({ gridRef }) => {
  const history = useHistory();
  const location = useLocation();

  const { reviewCycleId: reviewCycle } = useQuery();
  const { data: revieweeListData } = useGetPOARevieweeList(reviewCycle);

  const columnDefs = useMemo(
    () =>
      REVIEWEE_LIST_COLUMN_DEF.map((column) => ({
        colId: column.id,
        field: column.id,
        id: column.id,
        displayName: column.displayName,
        minWidth: 220,
        hide: false,
        flex: column.displayName === TABLE_COLUMNS_DISPLAYNAME.REVIEWEE_NAME ? 3 : 1,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
        },
        headerValueGetter: () => column.displayName,
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
        sortable: true,
        valueGetter: (params) =>
          getCellValue(column.displayName as keyof typeof TABLE_COLUMNS_DISPLAYNAME, params.data)?.toLowerCase(),
        cellRenderer: PlanOfActionCellRenderer,
        filter: 'agTextColumnFilter',
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [REVIEWEE_LIST_COLUMN_DEF]
  );

  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW;
    const viewId = 'MyReviews';
    const tabId = 'Explore';
    const uniqueId = 'ActionPlanGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const handleCellClick = ({ data }) => {
    const {
      employee: { displayName, id: revieweeId },
    } = data;
    const { search } = location;
    const queryParams = new URLSearchParams(search);
    const path = L3_PATHS.team.directReports.review.actionPlanEdit;
    queryParams.set('revieweeName', displayName);
    queryParams.set('revieweeId', revieweeId);
    queryParams.set('reviewCycle', reviewCycle as string);
    history.push({
      pathname: path,
      search: queryParams.toString(),
    });
  };

  return (
    <Card className="review" style={{ padding: '12px' }}>
      <AgGridTable
        ref={gridRef}
        gridStatePersistenceKey={gridStatePersistenceKey}
        sideBar={sideBar}
        columnDefs={columnDefs}
        rowData={revieweeListData}
        kebabMenuIsRowSpecific={false}
        domLayout="autoHeight"
        animateRows={false}
        rowHeight={74}
        containerAutoHeight
        pagination
        paginationPageSize={10}
        showSidebarToggle={false}
        onCellClicked={handleCellClick}
      />
    </Card>
  );
};

export default ReviewListView;
